// Contrail V3.9.0 — snooze alert notifications (30 min, 1 h, 6 h, till tomorrow 07:00 Fiji, custom) or turn them off.
// Personal (this account only). Events are still recorded and listed; while quiet there is no unread badge and no alert
// emails to this person. Used in the desktop Events tab and the phone app's Alerts tab.
import { useEffect, useRef, useState } from "react";
import { quietNow, setSnooze, tomorrowMorning, useUserPrefs } from "../lib/prefs";

const PRESETS: [string, number][] = [["30 minutes", 30], ["1 hour", 60], ["6 hours", 360]];

export default function SnoozeMenu({ className = "" }: { className?: string }) {
  const prefs = useUserPrefs();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [, tick] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30_000); return () => clearInterval(t); }, []);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const q = quietNow(prefs);
  const run = async (until: Date | null, muted = false) => {
    setErr(null);
    try { await setSnooze(until, muted); setOpen(false); } catch (e: any) { setErr(e.message); }
  };
  const customMin = new Date(Date.now() + 5 * 60_000);
  const local = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);

  return (
    <div className={`snz-wrap ${className}`} ref={box}>
      <button type="button" className={`btn-ghost small snz-btn ${q.quiet ? "on" : ""}`} onClick={() => setOpen((o) => !o)} aria-expanded={open}
        title={q.quiet ? `${q.label} — click to change` : "Snooze alert notifications"}>
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />{q.quiet && <path d="m3 3 18 18" />}</svg>
        <span className="snz-txt">{q.quiet ? (q.label.startsWith("Snoozed until") ? q.label.replace("Snoozed until ", "until ") : "Off") : "Snooze"}</span>
      </button>
      {open && (
        <div className="snz-menu" role="menu">
          <p className="sub">Alerts are still recorded. While snoozed you get no badge and no alert emails.</p>
          {PRESETS.map(([l, m]) => <button key={l} type="button" role="menuitem" onClick={() => run(new Date(Date.now() + m * 60_000))}>Snooze {l}</button>)}
          <button type="button" role="menuitem" onClick={() => run(tomorrowMorning())}>Till tomorrow (07:00)</button>
          <div className="snz-custom">
            <input type="datetime-local" value={custom} min={local(customMin)} onChange={(e) => setCustom(e.target.value)} aria-label="Snooze until" />
            <button type="button" className="btn-ghost small" disabled={!custom} onClick={() => run(new Date(custom))}>Set</button>
          </div>
          <button type="button" role="menuitem" onClick={() => run(null, true)}>Turn notifications off</button>
          {q.quiet && <button type="button" role="menuitem" className="snz-resume" onClick={() => run(null, false)}>Resume notifications now</button>}
          {err && <div className="error">{err}</div>}
        </div>
      )}
    </div>
  );
}
